import {EEntityType} from 'shared/constants/entities';
import {createReducer} from 'store/config/creators';
import {TAction} from 'store/config/types';

import {DataActions} from './data-constants';
import {
    TDataResetPayload,
    TDataSetFormModePayload,
    TDataSetInitialDataPayload,
    TDataSetIsEditablePayload,
    TDataSetPayload,
    IDataState,
    IRemoveDocumentScansPayload,
    TFileInfo,
    IPropertyData,
    TResetFormDataPayload,
    ISetDocumentScansDataPayload,
    TSetFilterLoadingPayload,
    TSetFormInstancePayload,
    TSetUsedAdditionalOptionIdPayload,
    TSetUsedAdditionalOptionsPayload,
    ITableEntityData,
    TUpdateFormDataPayload,
    TSetSelectedSubsection,
    TSetFilterPayload,
    TSetDataLoadingPayload,
} from './data-types';

const initialState: IDataState = {};

export const dataReducer = createReducer(initialState, {
    [DataActions.SET_DATA]: (
        state: IDataState,
        {payload}: TAction<TDataSetPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            loading: !!payload.loading,
            isError: payload.isError,
            [payload.entityType]: {
                ...(state[payload.entityName]?.[payload.entityType] || {}),
                ...(payload.data || {}),
                entityId: payload.entityId,
            },
        },
    }),
    [DataActions.RESET_DATA]: (
        state: IDataState,
        {payload}: TAction<TDataResetPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            loading: !!payload.loading,
            isEditable: undefined,
            [payload.entityType]: undefined,
        },
    }),
    [DataActions.RESET_INITIAL_DATA]: (
        state: IDataState,
        {payload}: TAction<TDataResetPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            loading: !!payload.loading,
            [payload.entityType]: {
                initialRows: undefined,
            },
        },
    }),

    [DataActions.RESET_ALL_DATA_WITH_EXCEPTION]: ( // reset всего, кроме заданного entityName
        state: IDataState,
        {payload}: TAction<Exclude<TDataResetPayload, 'entityType'>>,
    ) => {
        const currentEntityNameData = state[payload.entityName];
        if (!currentEntityNameData) return state;
        return {
            [payload.entityName]: {
                ...currentEntityNameData,
                loading: !!payload.loading,
            },
        };
    },
    [DataActions.SET_FORM_MODE]: (
        state: IDataState,
        {payload}: TAction<TDataSetFormModePayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [payload.entityType]: {
                ...(state[payload.entityName]?.[payload.entityType] || {}),
                formMode: payload.formMode,
            },
        },
    }),
    [DataActions.SET_IS_EDITABLE]: (
        state: IDataState,
        {payload}: TAction<TDataSetIsEditablePayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [payload.entityType]: {
                ...(state[payload.entityName]?.[payload.entityType] || {}),
                isEditable: payload.isEditable,
            },
        },
    }),
    [DataActions.UPDATE_FORM_DATA]: (state: IDataState, {payload}: TAction<TUpdateFormDataPayload>) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [EEntityType.FORM]: {
                ...(state[payload.entityName][EEntityType.FORM] || {}),
                data: {...(state[payload.entityName][EEntityType.FORM]?.data || {}), ...payload.data},
            },
        },
    }),
    [DataActions.RESET_FORM_DATA]: (state: IDataState, {payload}: TAction<TResetFormDataPayload>) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [EEntityType.FORM]: {},
        },
    }),
    [DataActions.SET_FORM_INSTANCE]: (state: IDataState, {payload}: TAction<TSetFormInstancePayload>) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [EEntityType.FORM]: {
                ...(state[payload.entityName]?.[EEntityType.FORM] || {}),
                formInstance: payload.form,
            },
        },
    }),
    [DataActions.SET_INITIAL_FORM_DATA]: (state: IDataState, {payload}: TAction<TDataSetInitialDataPayload>) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [EEntityType.FORM]: {
                ...(state[payload.entityName][EEntityType.FORM] || {}),
                data: state[payload.entityName][EEntityType.FORM].initialData,
                isEditable: payload.isEditable,
            },
        },
    }),
    [DataActions.SET_FORM_USED_ADDITIONAL_OPTION_ID]: (
        state: IDataState, {payload}: TAction<TSetUsedAdditionalOptionIdPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [payload.entityType || EEntityType.FORM_FIELDS_DATA]: payload.entityType ? {
                ...(state[payload.entityName]?.[payload.entityType] || {}),
                formUsedAdditionalOptionId: payload.optionId,
            } : {
                ...state[payload.entityName]?.[EEntityType.FORM_FIELDS_DATA],
                [payload.entityNameChild]: {
                    formUsedAdditionalOptionId: payload.optionId,
                },
            },
        },
    }),
    [DataActions.SET_FORM_USED_ADDITIONAL_OPTIONS]: (
        state: IDataState, {payload}: TAction<TSetUsedAdditionalOptionsPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            loading: !!payload.loading,
            FORM_FIELDS_DATA: {
                ...(state[payload.entityName].FORM_FIELDS_DATA || {}),
                options: {
                    ...(state[payload.entityName].FORM_FIELDS_DATA?.options || {}),
                    [payload.entityType]: payload.data,
                },
            },
        },
    }),
    [DataActions.RESET_FORM_USED_ADDITIONAL_OPTIONS]: (
        state: IDataState, {payload}: TAction<TSetUsedAdditionalOptionIdPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            FORM_FIELDS_DATA: {
                ...(initialState[payload.entityName]?.FORM_FIELDS_DATA || {}),
            },
        },
    }),
    [DataActions.SET_DATA_LOADING_STATE]: (
        state: IDataState, {payload}: TAction<TSetDataLoadingPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            loading: payload.loading
        },
    }),
    [DataActions.SET_FILTER_LOADING_STATE]: (
        state: IDataState, {payload}: TAction<TSetFilterLoadingPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [EEntityType.FILTER]: {
                ...(state[payload.entityName]?.[EEntityType.FILTER] || {}),
                loading: payload.loading,
            },
        },
    }),
    [DataActions.SET_FILTER]: (
        state: IDataState, {payload}: TAction<TSetFilterPayload>,
    ) => ({
        ...state,
        [payload.entityName]: {
            ...(state[payload.entityName] || {}),
            [EEntityType.FILTER]: {
                ...(state[payload.entityName]?.[EEntityType.FILTER] || {}),
                data: payload.data,
            },
        },
    }),
    [DataActions.SET_SELECTED_SUBSECTION]: (
        state: IDataState,
        {payload}: TAction<TSetSelectedSubsection>,
    ) => ({
        ...state,
        selectedSubsection: payload,
    }),
    [DataActions.SET_NOTIFICATION_DURATION]: (
        state: IDataState,
        {payload}: TAction<IPropertyData>,
    ) => ({...state, ...payload}),
    [DataActions.SET_DOCUMENT_SCANS_DATA]: (
        state: IDataState,
        {payload}: TAction<ISetDocumentScansDataPayload>,
    ) => {
        const {
            entityName, entityType, docId, scans,
        } = payload;
        if (entityType) {
            const {rows: tableRows = []} = state[entityName]?.[entityType] as ITableEntityData ?? {};

            const updatedTableRows = tableRows?.map(tableRow => {
                if (tableRow.id === docId) {
                    return {
                        ...tableRow,
                        scanDocumentAttachments: scans,
                    };
                }
                return tableRow;
            });

            if (!updatedTableRows) return {...state};

            return {
                ...state,
                [entityName]: {
                    ...(state[entityName] ?? {}),
                    [entityType]: {
                        ...(state[entityName]?.[entityType] ?? {}),
                        rows: updatedTableRows,
                    },
                },
            };
        }

        return {...state};
    },
    [DataActions.REMOVE_DOCUMENT_SCANS]: (
        state: IDataState,
        {payload}: TAction<IRemoveDocumentScansPayload>,
    ) => {
        const {
            entityName, entityType, docId, attachmentsIds,
        } = payload;
        if (entityType) {
            const {rows: tableRows = []} = state[entityName]?.[entityType] as ITableEntityData ?? {};

            const updatedTableRows = tableRows?.map(tableRow => {
                if (tableRow.id === docId) {
                    const scanDocumentAttachments = (tableRow.scanDocumentAttachments ?? []) as TFileInfo[];

                    const scanDocumentAttachmentsFiltered = scanDocumentAttachments
                        .filter(attachment => !attachmentsIds.includes(`${attachment.attachmentId}`));

                    return {
                        ...tableRow,
                        scanDocumentAttachments: scanDocumentAttachmentsFiltered,
                    };
                }

                return tableRow;
            });

            if (!updatedTableRows) return {...state};

            return {
                ...state,
                [entityName]: {
                    ...(state[entityName] ?? {}),
                    [entityType]: {
                        ...(state[entityName]?.[entityType] ?? {}),
                        rows: updatedTableRows,
                    },
                },
            };
        }

        return {...state};
    },
});
